import {
    Badge,
    Button,
    Heading,
    Input,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    Select,
    Spinner,
    Table,
    Tbody,
    Td,
    Th,
    Thead,
    Tr,
    useToast
} from "@chakra-ui/react";
import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import { useEffect, useState } from "react";
import { FaCalendarCheck, FaCopy } from "react-icons/fa";
import {
    copyLastWeekSchedule,
    fetchAllRoutes,
    fetchCourierWeekSchedule,
    insertScheduleBatch,
    updateSchedule
} from "../../api";
import { useTableColors } from "../../Utils";

dayjs.extend(utc);
dayjs.extend(timezone);

const daysOfWeek = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

const ScheduleModal = ({ isOpen, onClose, courier }) => {
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [weekStart, setWeekStart] = useState(dayjs().startOf("week").add(1, "day"));
    const [routes, setRoutes] = useState([]);
    const [schedule, setSchedule] = useState([]);

    const toast = useToast();
    const { theadBg, weekRowDeletedBg } = useTableColors();

    const loadWeek = async (start) => {
        if (!courier?._id) return;

        setLoading(true);
        try {
            const res = await fetchCourierWeekSchedule(courier._id, start.format("YYYY-MM-DD"));
            const fetched = res.success ? res.data : [];

            const map = {};
            fetched.forEach(s => {
                map[dayjs(s.date).format("YYYY-MM-DD")] = s;
            });

            const fullWeek = daysOfWeek.map((_, i) => {
                const date = start.clone().add(i, "day").format("YYYY-MM-DD");
                const existing = map[date];

                return {
                    _id: existing?._id || null,
                    date,
                    startTime: existing?.startTime || "",
                    routeId: existing?.routeId?._id || "",
                    dayOff: existing?.dayOff || false,
                };
            });

            setSchedule(fullWeek);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!isOpen || !courier?._id) return;

        fetchAllRoutes(1, 999999).then(res => {
            if (res.success) setRoutes(res.data.routes);
        });

        loadWeek(weekStart);
    }, [isOpen, courier]);

    const handleChange = (index, field, value) => {
        const copy = [...schedule];
        copy[index][field] = value;

        if (field === "dayOff" && value) {
            copy[index].startTime = "";
            copy[index].routeId = "";
        }

        setSchedule(copy);
    };

    const hasChanges = schedule.some(d =>
        d._id || (!d.dayOff && d.routeId && d.startTime)
    );

    const handleSave = async () => {
        setSaving(true);
        const snapshot = [...schedule];
        onClose(); // optimistic close

        try {
            const inserts = [];

            for (const day of schedule) {
                const laDate = dayjs.tz(day.date, "America/Los_Angeles").startOf("day").toDate();

                if (day._id) {
                    await updateSchedule(day._id, {
                        date: laDate,
                        routeId: day.dayOff ? null : day.routeId,
                        startTime: day.dayOff ? null : day.startTime,
                        dayOff: !!day.dayOff,
                    });
                } else {
                    // ALWAYS insert missing day (work OR day off)
                    inserts.push({
                        date: laDate,
                        routeId: day.dayOff ? null : day.routeId,
                        startTime: day.dayOff ? null : day.startTime,
                        dayOff: !!day.dayOff,
                    });
                }
            }

            if (inserts.length) {
                await insertScheduleBatch({
                    courierId: courier._id,
                    days: inserts,
                });
            }

            toast({ title: "Schedule saved", status: "success", duration: 3000 });
        } catch {
            setSchedule(snapshot); // rollback
            toast({ title: "Failed to save schedule", status: "error", duration: 3000 });
        } finally {
            setSaving(false);
        }
    };

    const handleCopyLastWeek = async () => {
        setLoading(true);
        try {
            await copyLastWeekSchedule(courier._id, weekStart.format("YYYY-MM-DD"));
            await loadWeek(weekStart);
            toast({ title: "Last week copied", status: "success", duration: 3000 });
        } catch {
            toast({ title: "Copy failed", status: "error", duration: 3000 });
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} size="5xl">
            <ModalOverlay />
            <ModalContent>
                <ModalHeader>Manage Schedule — {courier?.name}</ModalHeader>
                <ModalCloseButton />
                <ModalBody>
                    {loading ? <Spinner /> : (
                        <>
                            <Heading size="md" mb={3}>
                                Select Week <Badge colorScheme="green">Monday start</Badge>
                            </Heading>

                            <Input
                                type="date"
                                mb={4}
                                value={weekStart.format("YYYY-MM-DD")}
                                onChange={e => {
                                    const d = dayjs(e.target.value);
                                    const monday = d.day() === 0
                                        ? d.subtract(6, "day")
                                        : d.startOf("week").add(1, "day");
                                    setWeekStart(monday);
                                    loadWeek(monday);
                                }}
                            />

                            <Table>
                                <Thead bg={theadBg}>
                                    <Tr>
                                        <Th>Day</Th>
                                        <Th>Date</Th>
                                        <Th>Time</Th>
                                        <Th>Route</Th>
                                        <Th>Day Off</Th>
                                    </Tr>
                                </Thead>
                                <Tbody>
                                    {schedule.map((d, i) => (
                                        <Tr
                                            key={i}
                                            opacity={d.dayOff ? 0.5 : 1}
                                            bg={d.dayOff ? weekRowDeletedBg : undefined}
                                        >
                                            <Td>{daysOfWeek[i]}</Td>
                                            <Td>{d.date}</Td>
                                            <Td>
                                                <Input
                                                    type="time"
                                                    value={d.startTime}
                                                    disabled={d.dayOff}
                                                    onChange={e => handleChange(i, "startTime", e.target.value)}
                                                />
                                            </Td>
                                            <Td>
                                                <Select
                                                    value={d.routeId}
                                                    disabled={d.dayOff}
                                                    onChange={e => handleChange(i, "routeId", e.target.value)}
                                                >
                                                    <option value="">Select</option>
                                                    {routes.map(r => (
                                                        <option key={r._id} value={r._id}>{r.name}</option>
                                                    ))}
                                                </Select>
                                            </Td>
                                            <Td>
                                                <input
                                                    type="checkbox"
                                                    checked={d.dayOff}
                                                    onChange={e => handleChange(i, "dayOff", e.target.checked)}
                                                />
                                            </Td>
                                        </Tr>
                                    ))}
                                </Tbody>
                            </Table>
                        </>
                    )}
                </ModalBody>

                <ModalFooter>
                    <Button onClick={onClose} isDisabled={loading || saving} mr={3}>
                        Cancel
                    </Button>
                    <Button
                        size="sm"
                        colorScheme="purple"
                        leftIcon={<FaCopy />}
                        onClick={handleCopyLastWeek}
                        isDisabled={loading || saving}
                        mr={3}
                    >
                        Copy Last Week
                    </Button>
                    <Button
                        size="sm"
                        colorScheme="blue"
                        leftIcon={<FaCalendarCheck />}
                        onClick={handleSave}
                        isDisabled={!hasChanges || saving}
                        isLoading={saving}
                    >
                        Save
                    </Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
};

export default ScheduleModal;
